import React, { useState, useEffect } from "react";
import {
  api, DeckBrief, IntakeData, ProjectSlide,
  SlideRecommendation, SlidePreview,
} from "../api/client";

const F = "'Roboto Mono', monospace";

interface DeckBuilderProps {
  intakeData: Partial<IntakeData> | null;
  deckBrief: DeckBrief | null;
  isLoadingBrief: boolean;
  sessionId: string | null;
  selectedSlides: ProjectSlide[];
  onSlideSelect: (slide: ProjectSlide) => void;
  onReorderSlides: (slides: ProjectSlide[]) => void;
  onBuild: () => void;
  isBuilding: boolean;
  deckId: string | null;
  downloadUrl: string | null;
  onRevision: (newDeckId: string, newDownloadUrl: string) => void;
}

export const DeckBuilder: React.FC<DeckBuilderProps> = ({
  intakeData,
  deckBrief,
  isLoadingBrief,
  sessionId,
  selectedSlides,
  onSlideSelect,
  onReorderSlides,
  onBuild,
  isBuilding,
  deckId,
  downloadUrl,
  onRevision,
}) => {
  const [recommendations, setRecommendations] = useState<SlideRecommendation[]>([]);
  const [isLoadingRecs, setIsLoadingRecs] = useState(false);
  const [builtSlides, setBuiltSlides] = useState<SlidePreview[]>([]);
  const [driveUrl, setDriveUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isRevising, setIsRevising] = useState(false);

  // Fetch recommendations when brief is ready
  useEffect(() => {
    if (!deckBrief || !sessionId) return;
    setIsLoadingRecs(true);
    api.recommendSlides(sessionId, deckBrief)
      .then((data) => setRecommendations(data.recommendations))
      .catch(() => {})
      .finally(() => setIsLoadingRecs(false));
  }, [deckBrief, sessionId]);

  // Load built deck preview
  useEffect(() => {
    if (!deckId) {
      setBuiltSlides([]);
      setDriveUrl(null);
      return;
    }
    api.previewDeck(deckId).then((d) => setBuiltSlides(d.slides)).catch(() => {});
  }, [deckId]);

  const moveSlide = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= selectedSlides.length) return;
    const updated = [...selectedSlides];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onReorderSlides(updated);
  };

  const handleDrive = async () => {
    if (!deckId) return;
    setIsUploading(true);
    const w = window.open("about:blank", "_blank");
    try {
      const result = await api.uploadToDrive(deckId);
      setDriveUrl(result.google_drive_url);
      if (w) w.location.href = result.google_drive_url;
    } catch {
      if (w) w.close();
    } finally {
      setIsUploading(false);
    }
  };

  const handleRevision = async () => {
    if (!sessionId || !feedback.trim()) return;
    setIsRevising(true);
    try {
      const result = await api.reviseDeck(sessionId, feedback.trim());
      setBuiltSlides(result.slides);
      setFeedback("");
      setDriveUrl(null);
      onRevision(result.deck_id, api.getDeckDownloadUrl(result.deck_id));
    } catch {} finally {
      setIsRevising(false);
    }
  };

  const isRecSelected = (r: SlideRecommendation) =>
    selectedSlides.some(
      (s) => s.file_id === r.file_id && s.slide_number === r.slide_number
    );

  const handleRecClick = (r: SlideRecommendation) => {
    onSlideSelect({
      file_id: r.file_id,
      file_name: r.file_name,
      slide_number: r.slide_number,
      title: r.content_preview.substring(0, 60),
      file_type: "pptx",
      thumbnail: null,
    });
  };

  const hasAnything = intakeData || deckBrief || selectedSlides.length > 0;

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Empty state */}
        {!hasAnything && (
          <div style={styles.empty}>
            <div style={styles.emptyTitle}>DECK BUILDER</div>
            <div style={styles.rule} />
            <p style={styles.emptyText}>
              Fill in the brief on the left. Your deck will take shape here.
            </p>
          </div>
        )}

        {/* Brief summary */}
        {(intakeData || deckBrief) && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>BRIEF</div>
            {intakeData?.client_name && (
              <div style={styles.briefClient}>{intakeData.client_name}</div>
            )}
            {intakeData?.products && intakeData.products.length > 0 && (
              <div style={styles.tagRow}>
                {intakeData.products.map((p) => (
                  <span key={p} style={styles.tag}>{p}</span>
                ))}
              </div>
            )}
            {isLoadingBrief && (
              <div style={styles.loadingText}>Generating brief...</div>
            )}
            {deckBrief && (
              <>
                {deckBrief.key_messages.length > 0 && (
                  <div style={styles.briefMessages}>
                    {deckBrief.key_messages.map((m, i) => (
                      <div key={i} style={styles.messageItem}>{m}</div>
                    ))}
                  </div>
                )}
                <div style={styles.briefStats}>
                  {deckBrief.slide_count} slides &mdash; {deckBrief.tone}
                </div>
              </>
            )}
          </div>
        )}

        {/* AI Recommendations */}
        {deckBrief && (recommendations.length > 0 || isLoadingRecs) && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>
              RECOMMENDED SLIDES
              {isLoadingRecs && <span style={styles.loadingText}> loading...</span>}
            </div>
            <div style={styles.recGrid}>
              {recommendations.map((r) => {
                const selected = isRecSelected(r);
                return (
                  <div
                    key={`${r.file_id}-${r.slide_number}`}
                    style={{
                      ...styles.recCard,
                      ...(selected ? styles.recCardSelected : {}),
                    }}
                    onClick={() => handleRecClick(r)}
                  >
                    {r.thumbnail_url ? (
                      <img
                        src={api.getThumbnailUrl(r.file_id, r.slide_number)}
                        alt=""
                        style={styles.recImg}
                      />
                    ) : (
                      <div style={styles.recPlaceholder}>{r.slide_number}</div>
                    )}
                    <div style={styles.recCaption}>
                      {r.file_name} — S{r.slide_number}
                    </div>
                    {selected && <div style={styles.selectedBadge}>✓</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected slides / deck plan */}
        {selectedSlides.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>
              DECK PLAN ({selectedSlides.length} SLIDES)
            </div>
            <div style={styles.planList}>
              {selectedSlides.map((s, i) => (
                <div key={`${s.file_id}-${s.slide_number}`} style={styles.planItem}>
                  <div style={styles.planNumber}>{i + 1}</div>
                  <img
                    src={api.getThumbnailUrl(s.file_id, s.slide_number)}
                    alt=""
                    style={styles.planThumb}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div style={styles.planInfo}>
                    <div style={styles.planTitle}>{s.file_name}</div>
                    <div style={styles.planMeta}>Slide {s.slide_number}</div>
                  </div>
                  <div style={styles.planActions}>
                    <button
                      onClick={() => moveSlide(i, -1)}
                      style={styles.moveBtn}
                      disabled={i === 0}
                    >▲</button>
                    <button
                      onClick={() => moveSlide(i, 1)}
                      style={styles.moveBtn}
                      disabled={i === selectedSlides.length - 1}
                    >▼</button>
                    <button
                      onClick={() => onSlideSelect(s)}
                      style={styles.removeBtn}
                    >✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Built deck preview */}
        {deckId && builtSlides.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionLabel}>GENERATED DECK</div>
            {builtSlides.map((slide) => (
              <div key={slide.slide_number} style={styles.builtSlide}>
                <div style={styles.builtHeader}>
                  <span style={styles.builtNum}>{slide.slide_number}</span>
                  <span style={styles.builtTitle}>{slide.title}</span>
                </div>
                {slide.body && (
                  <div style={styles.builtBody}>
                    {slide.body.split("\n").map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions bar */}
      <div style={styles.actions}>
        {deckId ? (
          <>
            {downloadUrl && (
              <a href={downloadUrl} style={styles.primaryBtn} download>
                DOWNLOAD
              </a>
            )}
            {driveUrl ? (
              <a href={driveUrl} target="_blank" rel="noopener noreferrer" style={styles.secondaryBtn}>
                DRIVE
              </a>
            ) : (
              <button onClick={handleDrive} style={styles.secondaryBtn} disabled={isUploading}>
                {isUploading ? "..." : "EXPORT"}
              </button>
            )}
            <div style={styles.revisionBar}>
              <input
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleRevision()}
                placeholder="Request changes..."
                style={styles.revisionInput}
                disabled={isRevising}
              />
              <button
                onClick={handleRevision}
                style={styles.secondaryBtn}
                disabled={isRevising || !feedback.trim()}
              >
                REVISE
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={styles.slideCount}>
              {selectedSlides.length} SLIDE{selectedSlides.length !== 1 ? "S" : ""}
            </div>
            <button
              onClick={onBuild}
              style={{
                ...styles.primaryBtn,
                opacity: !deckBrief || isBuilding ? 0.4 : 1,
              }}
              disabled={!deckBrief || isBuilding}
            >
              {isBuilding ? "BUILDING..." : "BUILD"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px",
  },
  empty: {
    textAlign: "center" as const,
    padding: "60px 0",
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: 0.5,
    marginBottom: 12,
    color: "#1a1a1a",
  },
  rule: {
    width: 30,
    height: 2,
    background: "#FF0000",
    margin: "0 auto 16px",
  },
  emptyText: {
    fontSize: 11,
    color: "#666",
  },
  section: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #E8E8E8",
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    letterSpacing: 0.5,
    marginBottom: 8,
    textTransform: "uppercase" as const,
    paddingBottom: 10,
    borderBottom: "1px solid #E8E8E8",
  },
  briefClient: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 6,
    color: "#1a1a1a",
  },
  tagRow: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap" as const,
    marginBottom: 6,
  },
  tag: {
    padding: "2px 6px",
    background: "#1a1a1a",
    color: "#fff",
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: 0.3,
    borderRadius: 3,
  },
  loadingText: {
    fontSize: 9,
    color: "#888",
    marginTop: 4,
  },
  briefMessages: {
    marginTop: 8,
  },
  messageItem: {
    fontSize: 10,
    color: "#333",
    lineHeight: 1.5,
    padding: "2px 0 2px 8px",
    borderLeft: "2px solid #E0E0E0",
    marginBottom: 3,
  },
  briefStats: {
    fontSize: 10,
    color: "#666",
    marginTop: 6,
  },

  /* Recommendations */
  recGrid: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 8,
  },
  recCard: {
    width: 120,
    border: "1px solid #E0E0E0",
    borderRadius: 6,
    cursor: "pointer",
    position: "relative" as const,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  recCardSelected: {
    border: "2px solid #FF0000",
  },
  recImg: {
    width: "100%",
    height: 72,
    objectFit: "cover" as const,
    display: "block",
    background: "#F5F5F5",
    borderRadius: "6px 6px 0 0",
  },
  recPlaceholder: {
    width: "100%",
    height: 72,
    background: "#F5F5F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "#888",
    borderRadius: "6px 6px 0 0",
  },
  recCaption: {
    fontSize: 7,
    padding: "3px 4px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    color: "#666",
  },
  selectedBadge: {
    position: "absolute" as const,
    top: 3,
    right: 3,
    background: "#FF0000",
    color: "#fff",
    fontSize: 8,
    fontWeight: 700,
    width: 16,
    height: 16,
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  /* Deck plan */
  planList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  planItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    border: "1px solid #E0E0E0",
    borderRadius: 6,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  planNumber: {
    fontSize: 12,
    fontWeight: 700,
    color: "#FF0000",
    minWidth: 20,
    textAlign: "center" as const,
  },
  planThumb: {
    width: 60,
    height: 36,
    objectFit: "cover" as const,
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    flexShrink: 0,
  },
  planInfo: {
    flex: 1,
    overflow: "hidden",
  },
  planTitle: {
    fontSize: 9,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    color: "#1a1a1a",
  },
  planMeta: {
    fontSize: 8,
    color: "#888",
  },
  planActions: {
    display: "flex",
    gap: 3,
    flexShrink: 0,
  },
  moveBtn: {
    width: 20,
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 7,
    cursor: "pointer",
    color: "#666",
    fontFamily: F,
  },
  removeBtn: {
    width: 20,
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 9,
    cursor: "pointer",
    color: "#FF0000",
    fontFamily: F,
  },

  /* Built deck */
  builtSlide: {
    border: "1px solid #E0E0E0",
    borderRadius: 6,
    marginBottom: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  builtHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    background: "#FAFAFA",
    borderBottom: "1px solid #E8E8E8",
    borderRadius: "6px 6px 0 0",
  },
  builtNum: {
    fontSize: 9,
    fontWeight: 700,
    color: "#FF0000",
  },
  builtTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  builtBody: {
    padding: "8px 10px 8px 28px",
    fontSize: 10,
    color: "#333",
    lineHeight: 1.6,
  },

  /* Actions bar */
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    borderTop: "2px solid #E0E0E0",
    flexShrink: 0,
    flexWrap: "wrap" as const,
  },
  slideCount: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    flex: 1,
    color: "#1a1a1a",
  },
  primaryBtn: {
    padding: "11px 22px",
    background: "#FF0000",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontWeight: 700,
    fontSize: 11,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
    textDecoration: "none",
    display: "inline-block",
    boxShadow: "0 1px 3px rgba(255,0,0,0.2)",
  },
  secondaryBtn: {
    padding: "11px 22px",
    background: "#fff",
    color: "#1a1a1a",
    border: "1px solid #D0D0D0",
    borderRadius: 4,
    fontWeight: 700,
    fontSize: 11,
    fontFamily: F,
    cursor: "pointer",
    letterSpacing: 0.5,
    textDecoration: "none",
    display: "inline-block",
  },
  revisionBar: {
    display: "flex",
    gap: 6,
    flex: 1,
  },
  revisionInput: {
    flex: 1,
    padding: "6px 10px",
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    fontSize: 10,
    fontFamily: F,
    outline: "none",
  },
};
